import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Sparkles, Flame } from "lucide-react";
import { HERO } from "@/data/content";
import { CTAPair } from "./CTAButtons";
import { MaskedLines } from "./Motion";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden">
      {/* Parallax background video */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <video
          data-testid="hero-video"
          className="w-full h-full object-cover object-center"
          src={HERO.video}
          poster={HERO.poster}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        />
      </motion.div>
      <div className="absolute inset-0 z-10 hero-vignette" />

      {/* Floating badges */}
      <motion.div style={{ opacity: contentOpacity }} className="absolute z-20 top-24 md:top-28 inset-x-0">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap gap-3">
          <motion.div
            data-testid="hero-badge-price"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="glass-gold inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs md:text-sm tracking-tight"
          >
            <Sparkles className="w-4 h-4 text-gold" />
            <span className="text-gold-light font-medium">Starting Under ₹1 Lakh</span>
          </motion.div>
          <motion.div
            data-testid="hero-badge-dates"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.7 }}
            className="glass inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs md:text-sm tracking-tight"
          >
            <Flame className="w-4 h-4 text-gold" />
            <span className="text-white/90 font-medium">Limited Wedding Dates Available</span>
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom-left content */}
      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="absolute inset-0 z-20 flex items-end">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-xs md:text-sm tracking-[0.3em] uppercase text-gold mb-5"
          >
            {HERO.overline}
          </motion.p>

          <h1 className="font-guj text-white font-semibold tracking-tight leading-[1.15] text-3xl sm:text-5xl lg:text-6xl xl:text-7xl max-w-5xl">
            <MaskedLines lines={HERO.headingLines} delay={0.35} stagger={0.13} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            className="mt-6 max-w-xl text-sm sm:text-base lg:text-lg text-white/70 leading-relaxed"
          >
            {HERO.subheading}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-9"
          >
            <CTAPair prefix="hero" waLabel="WhatsApp Now" igLabel="DM on Instagram" />
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 1, 1, 0.3], y: [0, 6, 0] }}
        transition={{ delay: 1.6, duration: 2.2, repeat: Infinity }}
        className="absolute z-20 bottom-6 right-6 md:right-12 text-white/50"
      >
        <ChevronDown className="w-6 h-6" />
      </motion.div>
    </section>
  );
};

export default Hero;
